import { useState, useEffect, useRef } from 'react';

// Supported languages with flags
const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'id', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
];

// Typewriter Hook
const useTypewriter = (text, speed = 50, startDelay = 0, enabled = true) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, startDelay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, startDelay, enabled]);

  return { displayedText, isComplete };
};

// Language Selection Screen Component
const LanguageSelectionScreen = ({ isActive, onSelectLanguage, selectedLanguage }) => {
  return (
    <div className={`onboarding-screen ${isActive ? 'active' : ''}`}>
      <div className="onboarding-content language-selection-content">
        <div className="language-icon-wrapper">
          <span className="language-globe">🌍</span>
        </div>
        <h2 className="language-title">Choose your language</h2>
        <p className="language-subtitle">Wähle deine Sprache</p>
        <div className="language-grid">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`language-select-btn ${selectedLanguage === lang.code ? 'selected' : ''}`}
              onClick={() => onSelectLanguage(lang.code)}
              data-testid={`onboarding-lang-${lang.code}`}
            >
              <span className="lang-flag">{lang.flag}</span>
              <span className="lang-name">{lang.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Growth Chart Component
const GrowthChart = ({ isActive, language, subtext }) => {
  const [animationProgress, setAnimationProgress] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setAnimationProgress(0);
      return;
    }

    const startDelay = setTimeout(() => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.02;
        if (progress >= 1) {
          progress = 1;
          clearInterval(interval);
        }
        setAnimationProgress(progress);
      }, 30);

      return () => clearInterval(interval);
    }, 1500);

    return () => clearTimeout(startDelay);
  }, [isActive]);

  const labels = {
    de: { consistency: 'Beständigkeit', perfection: 'Perfektion', time: 'Zeit', growth: 'Wachstum' },
    en: { consistency: 'Consistency', perfection: 'Perfection', time: 'Time', growth: 'Growth' },
    bs: { consistency: 'Dosljednost', perfection: 'Savršenstvo', time: 'Vrijeme', growth: 'Rast' },
    ar: { consistency: 'الثبات', perfection: 'الكمال', time: 'الوقت', growth: 'النمو' },
    id: { consistency: 'Konsistensi', perfection: 'Kesempurnaan', time: 'Waktu', growth: 'Pertumbuhan' },
    tr: { consistency: 'Tutarlılık', perfection: 'Mükemmellik', time: 'Zaman', growth: 'Gelişim' }
  };
  const l = labels[language] || labels.de;

  // Blue steady growth path (consistency)
  const generateSteadyPath = (progress) => {
    const points = [];
    const steps = 50;
    
    for (let i = 0; i <= steps * progress; i++) {
      const x = (i / steps) * 280 + 30;
      const t = i / steps;
      const y = 180 - (Math.pow(t, 1.5) * 130);
      points.push(`${i === 0 ? 'M' : 'L'} ${x} ${y}`);
    }
    
    return points.join(' ');
  };

  // Red irregular path (perfection attempts - up and down)
  const generateIrregularPath = (progress) => {
    const points = [];
    const steps = 50;
    
    for (let i = 0; i <= steps * progress; i++) {
      const x = (i / steps) * 280 + 30;
      const t = i / steps;
      // Irregular wave pattern that goes up and down
      const baseY = 180 - (t * 60); // slight overall upward trend
      const wave = Math.sin(t * 12) * 35 + Math.sin(t * 7) * 20 + Math.sin(t * 19) * 15;
      const y = Math.max(50, Math.min(175, baseY + wave));
      points.push(`${i === 0 ? 'M' : 'L'} ${x} ${y}`);
    }
    
    return points.join(' ');
  };

  return (
    <div className={`growth-chart ${animationProgress > 0 ? 'visible' : ''}`}>
      <svg viewBox="0 0 320 220" className="growth-svg">
        <g className="grid-lines">
          {[0, 1, 2, 3, 4].map(i => (
            <line 
              key={`h-${i}`}
              x1="30" 
              y1={40 + i * 35} 
              x2="310" 
              y2={40 + i * 35}
              stroke="rgba(255,255,255,0.1)"
              strokeDasharray="4,4"
            />
          ))}
        </g>

        <line x1="30" y1="180" x2="310" y2="180" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
        <line x1="30" y1="40" x2="30" y2="180" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />

        {/* Red irregular curve (perfection) */}
        <path
          d={generateIrregularPath(animationProgress)}
          fill="none"
          stroke="#ef4444"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.7"
          style={{ filter: 'drop-shadow(0 0 4px rgba(239, 68, 68, 0.4))' }}
        />

        {/* Blue steady curve (consistency) */}
        <path
          d={generateSteadyPath(animationProgress)}
          fill="none"
          stroke="#7dd3fc"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ filter: 'drop-shadow(0 0 8px rgba(125, 211, 252, 0.5))' }}
        />

        {animationProgress > 0.1 && (
          <circle
            cx={30 + animationProgress * 280}
            cy={180 - Math.pow(animationProgress, 1.5) * 130}
            r="5"
            fill="#7dd3fc"
            style={{ filter: 'drop-shadow(0 0 6px rgba(125, 211, 252, 0.8))' }}
          />
        )}

        <text x="170" y="205" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10" fontFamily="Inter, sans-serif">
          {l.time}
        </text>
        
        <text x="15" y="110" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10" fontFamily="Inter, sans-serif" transform="rotate(-90, 15, 110)">
          {l.growth}
        </text>

        {/* Legend */}
        {animationProgress > 0.7 && (
          <g className="legend" style={{ opacity: animationProgress > 0.85 ? 1 : 0, transition: 'opacity 0.5s' }}>
            <line x1="200" y1="15" x2="220" y2="15" stroke="#7dd3fc" strokeWidth="2" />
            <text x="225" y="18" fill="#7dd3fc" fontSize="9" fontFamily="Inter, sans-serif">{l.consistency}</text>
            
            <line x1="200" y1="28" x2="220" y2="28" stroke="#ef4444" strokeWidth="2" opacity="0.7" />
            <text x="225" y="31" fill="#ef4444" fontSize="9" fontFamily="Inter, sans-serif" opacity="0.7">{l.perfection}</text>
          </g>
        )}
      </svg>
      
      {/* Subtext */}
      {subtext && animationProgress > 0.9 && (
        <p className="chart-subtext" style={{ 
          opacity: animationProgress > 0.95 ? 1 : 0, 
          transition: 'opacity 0.5s',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.6)',
          fontSize: '0.75rem',
          fontStyle: 'italic',
          marginTop: '0.5rem'
        }}>
          {subtext}
        </p>
      )}
    </div>
  );
};

// Deed Card Widget - Realistic simulation of the app's deed cards
const DeedCardWidget = ({ isActive, language }) => {
  const [showWidget, setShowWidget] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fadeState, setFadeState] = useState('visible'); // 'visible', 'fade-out', 'fade-in'

  // Sample deeds with precise source references for each language
  const deedsData = {
    de: [
      { text: 'Bitte heute Allah um Vergebung.', source: 'Quran, Sure 2 (Al-Baqara), Vers 199', category: 'dua' },
      { text: 'Schenke jemandem ein aufrichtiges Lächeln.', source: 'Sahih al-Bukhari, Hadith Nr. 2989', category: 'charity' },
      { text: 'Rufe einen Freund oder Verwandten an.', source: 'Sahih Muslim, Hadith Nr. 2566', category: 'family' }
    ],
    en: [
      { text: 'Ask Allah for forgiveness today.', source: 'Quran, Surah 2 (Al-Baqarah), Ayah 199', category: 'dua' },
      { text: 'Give someone a sincere smile.', source: 'Sahih al-Bukhari, Hadith No. 2989', category: 'charity' },
      { text: 'Call a friend or relative.', source: 'Sahih Muslim, Hadith No. 2566', category: 'family' }
    ],
    bs: [
      { text: 'Zamoli Allaha za oprost danas.', source: 'Kur\'an, Sura 2 (Al-Baqara), Ajet 199', category: 'dua' },
      { text: 'Pokloni nekome iskren osmijeh.', source: 'Sahih al-Buhari, Hadis br. 2989', category: 'charity' },
      { text: 'Nazovi prijatelja ili rođaka.', source: 'Sahih Muslim, Hadis br. 2566', category: 'family' }
    ],
    ar: [
      { text: 'اطلب من الله المغفرة اليوم.', source: 'القرآن، سورة البقرة، الآية ١٩٩', category: 'dua' },
      { text: 'امنح شخصاً ابتسامة صادقة.', source: 'صحيح البخاري، حديث رقم ٢٩٨٩', category: 'charity' },
      { text: 'اتصل بصديق أو قريب.', source: 'صحيح مسلم، حديث رقم ٢٥٦٦', category: 'family' }
    ],
    id: [
      { text: 'Minta ampunan Allah hari ini.', source: 'Al-Quran, Surah 2 (Al-Baqarah), Ayat 199', category: 'dua' },
      { text: 'Berikan senyuman tulus kepada seseorang.', source: 'Sahih al-Bukhari, Hadits No. 2989', category: 'charity' },
      { text: 'Telepon teman atau kerabat.', source: 'Sahih Muslim, Hadits No. 2566', category: 'family' }
    ],
    tr: [
      { text: 'Bugün Allah\'tan af dile.', source: 'Kur\'an, Sure 2 (Bakara), Ayet 199', category: 'dua' },
      { text: 'Birine içten bir gülümseme ver.', source: 'Sahih Buhari, Hadis No. 2989', category: 'charity' },
      { text: 'Bir arkadaşını veya akrabanı ara.', source: 'Sahih Müslim, Hadis No. 2566', category: 'family' }
    ]
  };

  const deeds = deedsData[language] || deedsData.de;

  useEffect(() => {
    if (!isActive) {
      setShowWidget(false);
      setActiveIndex(0);
      setFadeState('visible');
      return;
    }

    const showTimer = setTimeout(() => setShowWidget(true), 600);

    return () => clearTimeout(showTimer);
  }, [isActive]);

  // Smooth auto-cycle through cards
  useEffect(() => {
    if (!isActive || !showWidget) return;
    
    const interval = setInterval(() => {
      // Start fade out
      setFadeState('fade-out');
      
      // After fade out, change content and fade in
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % 3);
        setFadeState('fade-in');
        
        // Reset to visible
        setTimeout(() => {
          setFadeState('visible');
        }, 400);
      }, 400);
    }, 4000);

    return () => clearInterval(interval);
  }, [isActive, showWidget]);

  const currentDeed = deeds[activeIndex];

  const getCategoryIcon = (cat) => {
    const icons = { dua: '🤲', charity: '💝', family: '👨‍👩‍👧' };
    return icons[cat] || '✨';
  };

  return (
    <div className={`deed-widget ${showWidget ? 'visible' : ''} ${language === 'ar' ? 'rtl' : ''}`}>
      <div className={`deed-widget-card ${fadeState}`}>
        <div className="deed-widget-header">
          <span className="deed-widget-icon">{getCategoryIcon(currentDeed.category)}</span>
        </div>
        <p className="deed-widget-text">{currentDeed.text}</p>
        <p className="deed-widget-source">{currentDeed.source}</p>
      </div>
      
      <div className="deed-widget-dots">
        {[0, 1, 2].map(i => (
          <div key={i} className={`deed-widget-dot ${i === activeIndex ? 'active' : ''}`} />
        ))}
      </div>
    </div>
  );
};

// Final Screen with Name Input and Gender Selection
const FinalScreen = ({ isActive, language, userName, onNameChange, userGender, onGenderChange }) => {
  const labels = {
    de: { 
      placeholder: 'Dein Vorname',
      text1: 'Heute nur eine Sache.',
      text2: 'Mit der richtigen Absicht.'
    },
    en: { 
      placeholder: 'Your first name',
      text1: 'Just one thing today.',
      text2: 'With the right intention.'
    },
    bs: { 
      placeholder: 'Tvoje ime',
      text1: 'Danas samo jedno djelo.',
      text2: 'Potpuno posvećeno.',
      text3: 'S iskrenom namjerom.'
    },
    ar: { 
      placeholder: 'اسمك الأول',
      text1: 'شيء واحد فقط اليوم.',
      text2: 'بالنية الصحيحة.'
    },
    id: { 
      placeholder: 'Nama depanmu',
      text1: 'Hanya satu hal hari ini.',
      text2: 'Dengan niat yang benar.'
    },
    tr: { 
      placeholder: 'Adın',
      text1: 'Bugün sadece bir şey.',
      text2: 'Doğru niyetle.'
    }
  };
  const l = labels[language] || labels.de;

  return (
    <div className={`final-screen-content ${isActive ? 'active' : ''} ${language === 'ar' ? 'rtl' : ''}`}>
      <div className="final-input-wrapper">
        <input
          type="text"
          placeholder={l.placeholder}
          value={userName}
          onChange={(e) => onNameChange(e.target.value)}
          className="final-name-input"
          data-testid="onboarding-name-input"
        />
      </div>
      
      {/* Gender Selection - Minimalistic Emoji Icons */}
      <div className="gender-selection">
        <button
          type="button"
          className={`gender-btn ${userGender === 'male' ? 'selected' : ''}`}
          onClick={() => onGenderChange('male')}
          data-testid="gender-male"
          aria-label="Male"
        >
          <span className="gender-icon">🧔</span>
        </button>
        <button
          type="button"
          className={`gender-btn ${userGender === 'female' ? 'selected' : ''}`}
          onClick={() => onGenderChange('female')}
          data-testid="gender-female"
          aria-label="Female"
        >
          <span className="gender-icon">🧕</span>
        </button>
      </div>
      
      <div className="final-text-lines">
        <p className="final-text-line">{l.text1}</p>
        {l.text3 ? (
          <>
            <p className="final-text-line">{l.text2}</p>
            <p className="final-text-line final-text-emphasis">{l.text3}</p>
          </>
        ) : (
          <p className="final-text-line final-text-emphasis">{l.text2}</p>
        )}
      </div>
    </div>
  );
};

// Single Onboarding Screen
const OnboardingScreen = ({ 
  label, 
  mainText, 
  highlightWords = [], 
  isActive,
  showChart = false,
  showSwipe = false,
  subtext = '',
  language,
  children 
}) => {
  const { displayedText: labelText } = useTypewriter(label, 40, 300, isActive);
  const { displayedText: mainDisplayed, isComplete } = useTypewriter(mainText, 35, 800, isActive);

  const renderMainText = () => {
    let result = mainDisplayed;
    
    highlightWords.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      result = result.replace(regex, `<em class="highlight">$1</em>`);
    });
    
    // Replace newlines with <br> tags
    result = result.replace(/\n/g, '<br/>');
    
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  return (
    <div className={`onboarding-screen ${isActive ? 'active' : ''} ${language === 'ar' ? 'rtl' : ''}`}>
      <div className="onboarding-content">
        <p className="onboarding-label">{labelText}</p>
        <h1 className={`onboarding-main-text ${showChart || showSwipe || children ? 'with-chart' : ''}`}>
          {renderMainText()}
          {!isComplete && <span className="typewriter-cursor">|</span>}
        </h1>
        {showChart && <GrowthChart isActive={isActive} language={language} subtext={subtext} />}
        {showSwipe && <DeedCardWidget isActive={isActive} language={language} />}
        {children}
      </div>
    </div>
  );
};

// All screen translations - NEW simplified flow
const SCREEN_TRANSLATIONS = {
  de: [
    { label: 'WILLKOMMEN BEI DAILY1', mainText: 'Ein sanfter Weg, Allah zurück in den Mittelpunkt deines Tages zu bringen.', highlights: ['Allah'] },
    { label: 'FÜR WEN IST DAILY1?', mainText: 'Für jeden, der konkrete islamische Taten in seinen Alltag integrieren möchte.', highlights: ['konkrete islamische Taten'] },
    { label: 'WAS MACHT DAILY1?', mainText: 'Jeden Tag eine gute Tat. Klein genug für deinen Alltag – wertvoll genug für dein Herz.', highlights: ['eine gute Tat', 'dein Herz'] },
    { label: 'LANGFRISTIGE ERGEBNISSE', mainText: 'Beständigkeit verändert mehr als Perfektion.\n\nEine kleine Tat pro Tag lässt dein Inneres wachsen – mit der Zeit.', highlights: ['Beständigkeit', 'dein Inneres wachsen'], showChart: true, subtext: 'Allah liebt die beständigen Taten.' },
    { label: 'FOKUS AUF EINE TAT', mainText: 'Du musst dein Leben nicht umstellen. Heute reicht nur eine aufrichtige Tat für Allah.', highlights: ['eine aufrichtige Tat', 'Allah'] },
    { label: 'EINE TAT, DIE ZU DEINEM TAG PASST', mainText: 'Manche Taten passen heute, andere morgen. Wähle, was gerade für dich möglich ist.', highlights: ['für dich möglich'], showSwipe: true },
    { label: 'LASS UNS BEGINNEN', mainText: '', type: 'final' }
  ],
  en: [
    { label: 'WELCOME TO DAILY1', mainText: 'A gentle way to bring Allah back to the center of your day.', highlights: ['Allah'] },
    { label: 'WHO IS DAILY1 FOR?', mainText: 'For anyone who wants to integrate concrete Islamic deeds into their daily life.', highlights: ['concrete Islamic deeds'] },
    { label: 'WHAT DOES DAILY1 DO?', mainText: 'One good deed each day. Small enough for your routine – valuable enough for your heart.', highlights: ['One good deed', 'your heart'] },
    { label: 'LONG-TERM RESULTS', mainText: 'Consistency changes more than perfection.\n\nOne small deed per day lets your inner self grow – over time.', highlights: ['Consistency', 'inner self grow'], showChart: true, subtext: 'Allah loves consistent deeds.' },
    { label: 'FOCUS ON ONE DEED', mainText: "You don't have to change your life. Today, one sincere deed for Allah is enough.", highlights: ['one sincere deed', 'Allah'] },
    { label: 'A DEED THAT FITS YOUR DAY', mainText: "Some deeds fit today, others tomorrow. Choose what's possible for you right now.", highlights: ['possible for you'], showSwipe: true },
    { label: "LET'S BEGIN", mainText: '', type: 'final' }
  ],
  bs: [
    { label: 'DOBRODOŠLI U DAILY1', mainText: 'Nježan način da vratiš Allaha u središte svog dana.', highlights: ['Allaha'] },
    { label: 'ZA KOGA JE DAILY1?', mainText: 'Za svakoga ko želi uključiti konkretna islamska djela u svoj svakodnevni život.', highlights: ['konkretna islamska djela'] },
    { label: 'ŠTA RADI DAILY1?', mainText: 'Svaki dan jedno dobro djelo. Dovoljno malo za tvoj raspored – dovoljno vrijedno za tvoje srce.', highlights: ['jedno dobro djelo', 'tvoje srce'] },
    { label: 'DUGOROČNI REZULTATI', mainText: 'Ustrajnost daje dugoročne rezultate.\n\nS vremenom, jedno djelo dnevno, pomaže tvojoj unutrašnjosti da raste.', highlights: ['Ustrajnost', 'tvojoj unutrašnjosti'], showChart: true, subtext: 'Allah voli dosljedna djela.' },
    { label: 'FOKUS NA JEDNO DJELO', mainText: 'Ne moraš promijeniti svoj život. Danas je dovoljno jedno iskreno djelo za Allaha.', highlights: ['jedno iskreno djelo', 'Allaha'] },
    { label: 'DJELO KOJE ODGOVARA TVOM DANU', mainText: 'Neka djela odgovaraju danas, druga sutra. Izaberi šta je trenutno moguće za tebe.', highlights: ['moguće za tebe'], showSwipe: true },
    { label: 'HAJDE DA POČNEMO', mainText: '', type: 'final' }
  ],
  ar: [
    { label: 'مرحباً بك في DAILY1', mainText: 'طريقة لطيفة لإعادة الله إلى مركز يومك.', highlights: ['الله'] },
    { label: 'لمن DAILY1؟', mainText: 'لكل من يريد دمج أعمال إسلامية ملموسة في حياته اليومية.', highlights: ['أعمال إسلامية ملموسة'] },
    { label: 'ماذا يفعل DAILY1؟', mainText: 'عمل صالح واحد كل يوم. صغير بما يكفي لروتينك – ثمين بما يكفي لقلبك.', highlights: ['عمل صالح واحد', 'لقلبك'] },
    { label: 'نتائج طويلة المدى', mainText: 'الثبات يغير أكثر من الكمال.\n\nعمل صغير واحد في اليوم يجعل داخلك ينمو – مع الوقت.', highlights: ['الثبات', 'داخلك ينمو'], showChart: true, subtext: 'الله يحب الأعمال المستمرة.' },
    { label: 'التركيز على عمل واحد', mainText: 'لا تحتاج لتغيير حياتك. اليوم، يكفي عمل صادق واحد لله.', highlights: ['عمل صادق واحد', 'لله'] },
    { label: 'عمل يناسب يومك', mainText: 'بعض الأعمال تناسب اليوم، وأخرى غداً. اختر ما هو ممكن لك الآن.', highlights: ['ممكن لك'], showSwipe: true },
    { label: 'لنبدأ', mainText: '', type: 'final' }
  ],
  id: [
    { label: 'SELAMAT DATANG DI DAILY1', mainText: 'Cara lembut untuk mengembalikan Allah ke pusat harimu.', highlights: ['Allah'] },
    { label: 'UNTUK SIAPA DAILY1?', mainText: 'Untuk siapa saja yang ingin mengintegrasikan amalan Islam konkret ke dalam kehidupan sehari-hari.', highlights: ['amalan Islam konkret'] },
    { label: 'APA YANG DILAKUKAN DAILY1?', mainText: 'Satu perbuatan baik setiap hari. Cukup kecil untuk rutinitasmu – cukup berharga untuk hatimu.', highlights: ['Satu perbuatan baik', 'hatimu'] },
    { label: 'HASIL JANGKA PANJANG', mainText: 'Konsistensi mengubah lebih dari kesempurnaan.\n\nSatu perbuatan kecil per hari membuat jiwamu tumbuh – seiring waktu.', highlights: ['Konsistensi', 'jiwamu tumbuh'], showChart: true, subtext: 'Allah mencintai amalan yang konsisten.' },
    { label: 'FOKUS PADA SATU AMALAN', mainText: 'Kamu tidak perlu mengubah hidupmu. Hari ini, satu amalan tulus untuk Allah sudah cukup.', highlights: ['satu amalan tulus', 'Allah'] },
    { label: 'AMALAN YANG SESUAI HARIMU', mainText: 'Beberapa amalan cocok hari ini, yang lain besok. Pilih apa yang mungkin untukmu saat ini.', highlights: ['mungkin untukmu'], showSwipe: true },
    { label: 'MARI MULAI', mainText: '', type: 'final' }
  ],
  tr: [
    { label: "DAILY1'E HOŞ GELDİN", mainText: "Allah'ı gününün merkezine geri getirmenin nazik bir yolu.", highlights: ["Allah'ı"] },
    { label: 'DAILY1 KİM İÇİN?', mainText: 'Somut İslami amelleri günlük hayatına entegre etmek isteyen herkes için.', highlights: ['Somut İslami amelleri'] },
    { label: 'DAILY1 NE YAPAR?', mainText: 'Her gün bir iyi amel. Rutinin için yeterince küçük – kalbin için yeterince değerli.', highlights: ['bir iyi amel', 'kalbin için'] },
    { label: 'UZUN VADELİ SONUÇLAR', mainText: 'Tutarlılık mükemmellikten daha fazla değiştirir.\n\nGünde bir küçük amel, içini zamanla büyütür.', highlights: ['Tutarlılık', 'içini'], showChart: true, subtext: 'Allah tutarlı amelleri sever.' },
    { label: 'BİR AMELE ODAKLAN', mainText: 'Hayatını değiştirmek zorunda değilsin. Bugün Allah için bir içten amel yeterli.', highlights: ['bir içten amel', 'Allah'] },
    { label: 'GÜNÜNE UYAN BİR AMEL', mainText: 'Bazı ameller bugüne uyar, diğerleri yarına. Şu an senin için mümkün olanı seç.', highlights: ['senin için mümkün'], showSwipe: true },
    { label: 'BAŞLAYALIM', mainText: '', type: 'final' }
  ]
};

// Button labels for all languages
const BUTTON_LABELS = {
  de: { continue: 'Weiter', start: 'Bismillah!', skip: 'Überspringen' },
  en: { continue: 'Continue', start: 'Bismillah!', skip: 'Skip' },
  bs: { continue: 'Dalje', start: 'Bismillah!', skip: 'Preskoči' },
  ar: { continue: 'التالي', start: 'بسم الله!', skip: 'تخطي' },
  id: { continue: 'Lanjut', start: 'Bismillah!', skip: 'Lewati' },
  tr: { continue: 'Devam', start: 'Bismillah!', skip: 'Atla' }
};

// Main Component
const OnboardingFlow = ({ onComplete, language: initialLanguage = 'de', onLanguageChange }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [userName, setUserName] = useState('');
  const [userGender, setUserGender] = useState('male'); // Default to male
  const isNavigatingRef = useRef(false);

  // Active language (after selection)
  const language = selectedLanguage || initialLanguage;
  
  // Get screens for selected language
  const currentScreens = SCREEN_TRANSLATIONS[language] || SCREEN_TRANSLATIONS.de;
  const btn = BUTTON_LABELS[language] || BUTTON_LABELS.de;
  
  // Total screens: 1 (language) + 7 (content) = 8
  const totalScreens = currentScreens.length + 1;
  const isLanguageScreen = currentScreen === 0;
  const contentScreenIndex = currentScreen - 1;

  // Show button logic
  useEffect(() => {
    setShowButton(false);
    
    // Language selection screen
    if (isLanguageScreen) {
      if (selectedLanguage) {
        setTimeout(() => setShowButton(true), 300);
      }
      return;
    }
    
    const screen = currentScreens[contentScreenIndex];
    if (!screen) return;
    
    // Final screen: show quickly
    if (screen.type === 'final') {
      const timer = setTimeout(() => setShowButton(true), 1000);
      return () => clearTimeout(timer);
    }
    
    // Swipe screen: longer delay for animation
    if (screen.showSwipe) {
      const timer = setTimeout(() => setShowButton(true), 4000);
      return () => clearTimeout(timer);
    }
    
    // Chart screen: longer delay
    if (screen.showChart) {
      const timer = setTimeout(() => setShowButton(true), 3500);
      return () => clearTimeout(timer);
    }
    
    // Default: after typewriter
    const delay = 2500;
    const timer = setTimeout(() => setShowButton(true), delay);
    return () => clearTimeout(timer);
  }, [currentScreen, selectedLanguage, isLanguageScreen, currentScreens, contentScreenIndex]);

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem('goodDeeds_language', langCode);
    if (onLanguageChange) {
      onLanguageChange(langCode);
    }
  };

  const handleContinue = () => {
    // Prevent rapid double-clicks using ref (synchronous check)
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    
    // Save user info on final screen
    if (!isLanguageScreen && currentScreens[contentScreenIndex]?.type === 'final') {
      if (userName) localStorage.setItem('goodDeeds_userName', userName);
      localStorage.setItem('goodDeeds_userGender', userGender);
    }
    
    if (currentScreen < totalScreens - 1) {
      setCurrentScreen(prev => prev + 1);
    } else {
      localStorage.setItem('goodDeeds_onboardingComplete', 'true');
      onComplete();
    }
    
    // Allow next click after transition completes
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 500);
  };

  const handleSkip = () => {
    if (selectedLanguage) {
      localStorage.setItem('goodDeeds_language', selectedLanguage);
    }
    localStorage.setItem('goodDeeds_onboardingComplete', 'true');
    onComplete();
  };

  const getButtonLabel = () => {
    if (isLanguageScreen) return btn.continue;
    if (currentScreens[contentScreenIndex]?.type === 'final') return btn.start;
    return btn.continue;
  };

  const isFinalScreen = !isLanguageScreen && currentScreens[contentScreenIndex]?.type === 'final';

  return (
    <div className="onboarding-container" data-testid="onboarding-flow">
      <div className="onboarding-background"></div>

      <div className="onboarding-progress">
        {Array.from({ length: totalScreens }).map((_, index) => (
          <div 
            key={index}
            className={`progress-dot ${index === currentScreen ? 'active' : ''} ${index < currentScreen ? 'completed' : ''}`}
          />
        ))}
      </div>

      {/* Language Selection Screen */}
      {isLanguageScreen && (
        <LanguageSelectionScreen 
          isActive={true}
          onSelectLanguage={handleLanguageSelect}
          selectedLanguage={selectedLanguage}
        />
      )}

      {/* Content Screens - Only render current screen */}
      {!isLanguageScreen && currentScreens[contentScreenIndex] && (() => {
        const screen = currentScreens[contentScreenIndex];
        
        // Final Screen with name input
        if (screen.type === 'final') {
          return (
            <div key={contentScreenIndex} className={`onboarding-screen active ${language === 'ar' ? 'rtl' : ''}`}>
              <div className="onboarding-content final-screen">
                <p className="onboarding-label">{screen.label}</p>
                <FinalScreen
                  isActive={true}
                  language={language}
                  userName={userName}
                  onNameChange={setUserName}
                  userGender={userGender}
                  onGenderChange={setUserGender}
                />
              </div>
            </div>
          );
        }
        
        // Standard Screen
        return (
          <OnboardingScreen
            key={contentScreenIndex}
            label={screen.label}
            mainText={screen.mainText}
            highlightWords={screen.highlights || []}
            isActive={true}
            showChart={screen.showChart}
            showSwipe={screen.showSwipe}
            subtext={screen.subtext || ''}
            language={language}
          />
        );
      })()}

      <button 
        className={`onboarding-continue ${showButton ? 'visible' : ''} ${isFinalScreen ? 'final-btn' : ''}`}
        onClick={handleContinue}
        disabled={!showButton}
        data-testid="onboarding-continue"
      >
        {isFinalScreen && <span className="btn-pulse-ring"></span>}
        {getButtonLabel()}
      </button>
    </div>
  );
};

export default OnboardingFlow;
